import React from 'react';
import type { Summary } from '../../types/messages';

interface Props {
  text: string;
  summary: Summary;
  includeTax: boolean;
  onArrangementRequest: () => void;
  isRequesting: boolean;
  isRequested: boolean;
}

const ArrangementConfirmationMessage = ({
  text,
  summary,
  includeTax,
  onArrangementRequest,
  isRequesting,
  isRequested
}: Props) => {
  const styles = getStyles();
  const { items, totalPrice, totalPriceWithTax } = summary;
  const displayTotal = includeTax ? totalPriceWithTax : totalPrice;

  const buttonText = isRequested ? '手配依頼済み' : isRequesting ? '処理中...' : '手配依頼をする';

  return (
    <div style={styles.container}>
      <div style={styles.messageText}>{text}</div>

      <div style={styles.summarySection}>
        <div style={styles.sectionTitle}>【選択内容】</div>
        <div style={styles.itemList}>
          {items.map(item => (
            <div key={item.category} style={styles.item}>
              <div style={styles.itemLabel}>{item.label}:</div>
              <div style={styles.itemContent}>
                <div>{item.text}</div>
                <div style={styles.itemPrice}>
                  料金: {item.price !== null ? `¥${item.price.toLocaleString()}` : 'undefined'}
                  {includeTax && item.priceWithTax !== null && item.priceWithTax !== item.price && (
                    <span>（税込 ¥{item.priceWithTax.toLocaleString()}）</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={styles.totalRow}>
        <span style={styles.totalLabel}>合計金額：</span>
        <span style={styles.totalPrice}>
          ¥{displayTotal.toLocaleString()}
          {includeTax ? '（税込）' : ''}
        </span>
      </div>

      <div style={styles.buttonSection}>
        <button
          type="button"
          onClick={onArrangementRequest}
          disabled={isRequesting || isRequested}
          style={{
            ...styles.primaryButton,
            ...(isRequested ? styles.buttonRequested : {}),
            ...(isRequesting || isRequested ? styles.buttonDisabled : {})
          }}
        >
          {buttonText}
        </button>
      </div>

      <div style={styles.disclaimer}>
        なお手配依頼をする時に弊社の利用約款、旅行業約款・手配旅行条件書、プライバシーポリシーに同意するものとします。
        本依頼を行う者は、本サービスを正当に利用する権限を有することを保証します。
      </div>
    </div>
  );
};

const getStyles = () => ({
  container: {
    backgroundColor: '#ffffff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    padding: '16px',
    display: 'flex' as const,
    flexDirection: 'column' as const,
    gap: '16px',
    maxWidth: '480px'
  },
  messageText: {
    fontSize: '14px',
    color: '#333',
    lineHeight: '1.5'
  },
  summarySection: {
    display: 'flex' as const,
    flexDirection: 'column' as const,
    gap: '12px'
  },
  sectionTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#333'
  },
  itemList: {
    display: 'flex' as const,
    flexDirection: 'column' as const,
    gap: '12px'
  },
  item: {
    display: 'flex' as const,
    flexDirection: 'column' as const,
    gap: '4px'
  },
  itemLabel: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#666'
  },
  itemContent: {
    fontSize: '13px',
    color: '#333',
    paddingLeft: '8px'
  },
  itemPrice: {
    fontSize: '12px',
    color: '#666',
    marginTop: '2px'
  },
  totalRow: {
    display: 'flex' as const,
    alignItems: 'baseline' as const,
    gap: '8px',
    paddingTop: '8px',
    borderTop: '1px solid #e0e0e0'
  },
  totalLabel: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#333'
  },
  totalPrice: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#333'
  },
  buttonSection: {
    display: 'flex' as const,
    gap: '12px',
    marginTop: '8px'
  },
  primaryButton: {
    padding: '12px 24px',
    fontSize: '14px',
    fontWeight: 600,
    color: '#ffffff',
    backgroundColor: '#1976d2',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer' as const
  },
  buttonRequested: {
    backgroundColor: '#9e9e9e'
  },
  buttonDisabled: {
    cursor: 'not-allowed' as const,
    opacity: 0.7
  },
  disclaimer: {
    fontSize: '11px',
    color: '#666',
    lineHeight: '1.5',
    padding: '12px',
    backgroundColor: '#f5f5f5',
    borderRadius: '4px'
  }
});

export default ArrangementConfirmationMessage;
